import React, { useState } from 'react';
import { useReallocateEmail, useAssignToMultipleAffaires, useUnlinkEmail, useDeleteEmailCopy } from '../../hooks/useTrackerEmails';
import type { TrackerEmail } from '../../types/tracker.types';
import './EmailReallocationModal.css';

interface EmailReallocationModalProps {
  email: TrackerEmail;
  currentAffaireId: number;
  onClose: () => void;
  onSuccess: () => void;
}

type ActionType = 'move' | 'duplicate' | 'unlink' | 'delete';

export const EmailReallocationModal: React.FC<EmailReallocationModalProps> = ({
  email,
  currentAffaireId,
  onClose,
  onSuccess
}) => {
  const [actionType, setActionType] = useState<ActionType>('move');
  const [targetAffaireId, setTargetAffaireId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const reallocateEmail = useReallocateEmail();
  const assignToMultiple = useAssignToMultipleAffaires();
  const unlinkEmail = useUnlinkEmail();
  const deleteEmailCopy = useDeleteEmailCopy();

  const isLoading = reallocateEmail.isPending || assignToMultiple.isPending || unlinkEmail.isPending || deleteEmailCopy.isPending;

  // Check if this email is a duplicate (has parent_email_id)
  const isDuplicate = !!(email as any).parent_email_id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (actionType === 'move' || actionType === 'duplicate') {
        const affaireIdNum = parseInt(targetAffaireId, 10);

        if (isNaN(affaireIdNum) || affaireIdNum < 1000) {
          setError('Le numéro d\'affaire doit contenir au moins 4 chiffres');
          return;
        }

        if (affaireIdNum === currentAffaireId) {
          setError('L\'email est déjà lié à cette affaire');
          return;
        }

        if (actionType === 'move') {
          await reallocateEmail.mutateAsync({
            emailId: email.id,
            targetAffaireId: affaireIdNum
          });
        } else {
          await assignToMultiple.mutateAsync({
            emailId: email.id,
            data: {
              affaire_ids: [affaireIdNum]
            }
          });
        }
      } else if (actionType === 'unlink') {
        await unlinkEmail.mutateAsync({ emailId: email.id });
      } else if (actionType === 'delete') {
        if (!confirmDelete) {
          setConfirmDelete(true);
          return;
        }
        await deleteEmailCopy.mutateAsync({ emailId: email.id });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getActionTitle = () => {
    switch (actionType) {
      case 'move': return 'Déplacer l\'email';
      case 'duplicate': return 'Dupliquer l\'email';
      case 'unlink': return 'Dissocier l\'email';
      case 'delete': return 'Supprimer l\'email';
    }
  };

  const getInfoMessage = () => {
    switch (actionType) {
      case 'move':
        return `L'email sera retiré du dossier #${currentAffaireId} et déplacé vers le nouveau dossier.`;
      case 'duplicate':
        return `Une copie de l'email sera créée dans le dossier cible. L'original restera dans le dossier #${currentAffaireId}.`;
      case 'unlink':
        return `L'email sera dissocié du dossier #${currentAffaireId} et retournera dans les emails non attribués.`;
      case 'delete':
        return isDuplicate
          ? `Cette copie de l'email sera supprimée définitivement. Les autres copies ne seront pas affectées.`
          : `L'email sera supprimé définitivement du dossier #${currentAffaireId}.`;
    }
  };

  const getAttachmentNote = () => {
    switch (actionType) {
      case 'move':
        return 'Les pièces jointes seront déplacées avec l\'email.';
      case 'duplicate':
        return 'Les pièces jointes seront copiées avec l\'email.';
      case 'unlink':
        return 'Les pièces jointes resteront associées à l\'email.';
      case 'delete':
        return 'Les pièces jointes seront également supprimées.';
    }
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      switch (actionType) {
        case 'move': return 'Déplacement...';
        case 'duplicate': return 'Duplication...';
        case 'unlink': return 'Dissociation...';
        case 'delete': return 'Suppression...';
      }
    }

    if (actionType === 'delete' && confirmDelete) {
      return 'Confirmer la suppression';
    }

    switch (actionType) {
      case 'move': return 'Déplacer';
      case 'duplicate': return 'Dupliquer';
      case 'unlink': return 'Dissocier';
      case 'delete': return 'Supprimer';
    }
  };

  const needsTargetAffaire = actionType === 'move' || actionType === 'duplicate';

  return (
    <div className="email-reallocation-modal-backdrop" onClick={handleBackdropClick}>
      <div className="email-reallocation-modal">
        <div className="email-reallocation-modal-header">
          <h3>{getActionTitle()}</h3>
          <button
            className="email-reallocation-modal-close"
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="email-reallocation-modal-content">
          {/* Action Type Toggle - 2x2 grid */}
          <div className="email-reallocation-action-grid">
            <button
              type="button"
              className={`email-reallocation-action-btn ${actionType === 'move' ? 'active' : ''}`}
              onClick={() => { setActionType('move'); setConfirmDelete(false); setError(null); }}
            >
              <span className="action-icon">↔️</span>
              <span className="action-label">Déplacer</span>
            </button>
            <button
              type="button"
              className={`email-reallocation-action-btn ${actionType === 'duplicate' ? 'active' : ''}`}
              onClick={() => { setActionType('duplicate'); setConfirmDelete(false); setError(null); }}
            >
              <span className="action-icon">📋</span>
              <span className="action-label">Dupliquer</span>
            </button>
            <button
              type="button"
              className={`email-reallocation-action-btn ${actionType === 'unlink' ? 'active' : ''}`}
              onClick={() => { setActionType('unlink'); setConfirmDelete(false); setError(null); }}
            >
              <span className="action-icon">🔗</span>
              <span className="action-label">Dissocier</span>
            </button>
            <button
              type="button"
              className={`email-reallocation-action-btn danger ${actionType === 'delete' ? 'active' : ''}`}
              onClick={() => { setActionType('delete'); setConfirmDelete(false); setError(null); }}
            >
              <span className="action-icon">🗑️</span>
              <span className="action-label">Supprimer</span>
            </button>
          </div>

          <div className="email-reallocation-info">
            <div className="email-reallocation-field">
              <label>Email:</label>
              <span className="email-subject">{email.subject || '(Sans sujet)'}</span>
            </div>

            <div className="email-reallocation-field">
              <label>Actuellement lié à:</label>
              <span className="current-affaire">#{currentAffaireId}</span>
            </div>

            {isDuplicate && (
              <div className="email-reallocation-field">
                <label>Type:</label>
                <span className="email-type-badge duplicate">Copie</span>
              </div>
            )}
          </div>

          {needsTargetAffaire && (
            <div className="email-reallocation-input-group">
              <label htmlFor="targetAffaireId">
                {actionType === 'move' ? 'Nouveau numéro d\'affaire:' : 'Numéro d\'affaire cible:'}
              </label>
              <input
                type="text"
                id="targetAffaireId"
                value={targetAffaireId}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setTargetAffaireId(value);
                  setError(null);
                }}
                placeholder="Ex: 68011"
                pattern="\d{4,}"
                required
                autoFocus
              />
            </div>
          )}

          {/* Info message based on action type */}
          <div className={`email-reallocation-info-message ${actionType}`}>
            <p>{getInfoMessage()}</p>
          </div>

          {email.has_attachments && (
            <p className="email-reallocation-attachments-note">
              {getAttachmentNote()}
            </p>
          )}

          {error && (
            <div className="email-reallocation-error">
              {error}
            </div>
          )}

          {actionType === 'delete' && confirmDelete && (
            <div className="email-reallocation-confirm-warning">
              ⚠️ Cette action est irréversible. Êtes-vous sûr de vouloir supprimer cet email ?
            </div>
          )}

          <div className="email-reallocation-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn-submit ${actionType}`}
              disabled={isLoading || (needsTargetAffaire && (!targetAffaireId || targetAffaireId.length < 4))}
            >
              {getSubmitButtonText()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailReallocationModal;
